import { Link, Navigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { CITIES, SERVICES, BRAND, GALLERY } from '@/data/content';
import { PortfolioImage } from '@/components/site/SiteBits';
import { Seo, Breadcrumbs, BUSINESS_ID, SITE_URL } from '@/components/site/Seo';
import FinalCTA from '@/components/site/FinalCTA';

export default function CityPage({ citySlug }) {
  const city = CITIES.find((c) => c.slug === citySlug);
  if (!city || !city.intro) return <Navigate to="/service-areas" replace />;

  const path = `/${city.slug}`;
  // Real completed projects in this city — only items with a verified location appear here
  const cityProjects = GALLERY.filter((g) => (g.location || '').toLowerCase().startsWith(city.name.toLowerCase()));
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Home repair and improvement',
      provider: { '@id': BUSINESS_ID },
      areaServed: { '@type': 'City', name: `${city.name}, TX` },
      url: `${SITE_URL}${path}`,
      description: city.seoDescription,
    },
  ];

  return (
    <div data-testid={`page-city-${city.slug}`}>
      <Seo title={city.seoTitle} description={city.seoDescription} path={path} jsonLd={jsonLd} />

      <section className="border-b border-brand-border bg-brand-bone">
        <div className="container-x py-14 lg:py-20">
          <Breadcrumbs
            items={[
              { label: 'Home', to: '/' },
              { label: 'Service Areas', to: '/service-areas' },
              { label: city.name },
            ]}
            testId={`breadcrumbs-${city.slug}`}
          />
          <div className="max-w-3xl">
            <div className="eyebrow mb-3">Service Area</div>
            <h1 className="h1">Home repair & handyman services in {city.name}, Texas.</h1>
            <div className="mt-6 space-y-5">
              {city.intro.map((p, i) => (
                <p key={i} className="body-lg">{p}</p>
              ))}
            </div>
            <div className="mt-8 flex gap-3 flex-col sm:flex-row">
              <Link to="/estimate" className="btn-primary" data-testid={`city-cta-${city.slug}`}>Request an Estimate</Link>
              <a href={BRAND.phoneHref} className="btn-secondary">Call {BRAND.phoneDisplay}</a>
            </div>
          </div>
        </div>
      </section>

      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-16 lg:py-24">
          <div className="eyebrow mb-3">Available in {city.name}</div>
          <h2 className="h2">Services we provide in {city.name}</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-brand-border">
            {SERVICES.map((s) => {
              const Icon = Icons[s.icon] || Icons.Hammer;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group p-7 border-r border-b border-brand-border bg-brand-bone hover:bg-brand-boneAlt transition-colors"
                  data-testid={`city-service-${city.slug}-${s.slug}`}
                >
                  <Icon size={22} strokeWidth={1.5} className="text-brand-maroon" />
                  <div className="mt-4 font-serif text-xl text-brand-charcoal">{s.title}</div>
                  <p className="mt-2 font-sans text-sm text-brand-muted leading-relaxed">{s.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {cityProjects.length > 0 && (
        <section className="border-b border-brand-border">
          <div className="container-x py-16 lg:py-24">
            <div className="eyebrow mb-3">Recent work</div>
            <h2 className="h2">Completed projects in {city.name}</h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`city-projects-${city.slug}`}>
              {cityProjects.map((g) => (
                <Link key={g.id} to={`/gallery/${g.id}`} className="group block border border-brand-border bg-brand-bone hover:border-brand-charcoal transition-colors" data-testid={`city-project-${g.id}`}>
                  <PortfolioImage src={g.cover || g.src} className="aspect-[4/3]" alt={g.title} />
                  <div className="p-5">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-brand-maroon">{g.category}{g.location ? ` · ${g.location}` : ''}</div>
                    <div className="mt-1 font-serif text-lg text-brand-charcoal group-hover:text-brand-maroon transition-colors">{g.title}</div>
                    <p className="mt-1 font-sans text-xs text-brand-muted leading-relaxed">{g.caption}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-b border-brand-border">
        <div className="container-x py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
          <div>
            <div className="eyebrow mb-3">Local knowledge</div>
            <h2 className="h2">What {city.name} homes deal with.</h2>
          </div>
          <ul className="space-y-5">
            {city.localNotes.map((note, i) => (
              <li key={i} className="flex items-start gap-3 border-b border-brand-border pb-5">
                <Icons.MapPin size={18} className="mt-1 text-brand-maroon flex-shrink-0" />
                <p className="font-sans text-base leading-relaxed text-brand-charcoal/90">{note}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-16 lg:py-24 max-w-3xl">
          <div className="eyebrow mb-3">Common questions</div>
          <h2 className="h2">{city.name} FAQs</h2>
          <div className="mt-10 space-y-0 border-t border-brand-border">
            {city.faq.map((f, i) => (
              <div key={i} className="py-6 border-b border-brand-border">
                <h3 className="font-serif text-xl text-brand-charcoal">{f.q}</h3>
                <p className="mt-2 font-sans text-base leading-relaxed text-brand-muted">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
